import { Component, Inject, OnInit } from '@angular/core';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  selectCurrentUser,
  selectIsPaid,
  User,
  PL_API_CONFIG,
  PlAPIConfig,
} from 'pl-core-services';
import { filter, map, switchMap } from 'rxjs/operators';
import { PlModalService, PlModalType } from '@app/common/services';
import { UserProfileService } from '@app/common/services/user-profile.service';
import { MatDialog, MatDialogState } from '@angular/material/dialog';
import { PlAccountService } from '@common/entities/pl-account.service';
import { selectAccountId } from '@app/common/store';
import { NavigationStart, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { fromSubscription } from '@app/subscription/store';
import { ProviderBaaSignatureComponent } from '../provider-baa-signature/provider-baa-signature.component';

@UntilDestroy()
@Component({
  selector: 'pl-provider-workspace',
  templateUrl: './provider-workspace.component.html',
  styleUrls: ['./provider-workspace.component.scss'],
})
export class ProviderWorkspaceComponent implements OnInit {
  user: User = {
    username: '',
    first_name: '',
  };

  isPaid = false;

  dialogRef: any;

  readonly lms_url = `${environment.login_url}/saml/sso/learnupon?sp=${environment.learnupon_url}&RelayState=Dashboard`;

  license$ = this.store.select(fromSubscription.selectCurrentLicenseName);

  canUpgrade$ = this.store.select(fromSubscription.selectCanUpgrade);

  private currentUser: User;

  constructor(
    @Inject(PL_API_CONFIG) private config: PlAPIConfig,
    private store: Store,
    private modal: PlModalService,
    private userService: UserProfileService,
    private dialog: MatDialog,
    private accountService: PlAccountService,
    private router: Router,
    private snackbar: MatSnackBar,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.store
      .select(selectCurrentUser)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.currentUser = user;
        this.user = user;
        this.userService.getUserProfile().subscribe((profile) => {
          if (!profile.welcome_date) {
            this.modal.open(PlModalType.PROVIDER_WELCOME_MODAL).subscribe();
            const newprofile = { ...profile, welcome_shown: true };
            this.userService.updateUserProfile(newprofile);
          }
        });
      });
    this.store
      .select(selectIsPaid)
      .pipe(untilDestroyed(this))
      .subscribe((isPaid) => {
        this.isPaid = isPaid;
      });

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(
          (event: any) =>
            event instanceof NavigationStart &&
            event.url.includes('#sign-success')
        )
      )
      .subscribe((_: any) => {
        if (
          this.dialogRef &&
          this.dialogRef.getState() === MatDialogState.OPEN
        ) {
          window.location.replace('/landing-home#');
          this.dialogRef.close();
          this.snackbar.open(
            'A copy will be emailed to you after you confirm your email in your mailbox',
            'X',
            {
              verticalPosition: 'top',
              panelClass: ['baa-sign-success-snackbar'],
            }
          );
        }
      });

    this.ensureBAASigned();
  }

  private ensureBAASigned() {
    this.store
      .select(selectAccountId)
      .pipe(
        filter((accountId) => !!accountId),
        switchMap((accountId) =>
          this.accountService
            .isAccountBaaSigned(accountId)
            .pipe(map((baaSigned) => [baaSigned, accountId] as const))
        ),
        untilDestroyed(this)
      )
      .subscribe(([baaSigned, accountId]) => {
        if (!baaSigned) {
          this.openBaaDialog(accountId);
        }
      });
  }

  get roomUrl(): string {
    return `${this.config.room}/${this.user?.username}`;
  }

  copyRoomUrl() {
    navigator.clipboard.writeText(this.roomUrl);
  }

  openBaaDialog(accountId: string) {
    this.dialogRef = this.dialog.open(ProviderBaaSignatureComponent, {
      minWidth: '1000px',
      height: '85%',
      disableClose: true,
      closeOnNavigation: false,
      data: {
        userUuid: this.user.uuid,
        accountUuid: accountId,
      },
    });
  }

  openSupportChat() {
    const zenDeskwidget = (window as any).zE;
    this.http
      .get(`${this.config.gateway}/auth/token/zendesk`)
      .subscribe((resp: any) => {
        if (resp.exists_in_zendesk) {
          zenDeskwidget(
            'messenger',
            'loginUser',
            (callback: (arg: any) => void) => {
              callback(resp.jwt);
            }
          );
          zenDeskwidget('messenger:set', 'conversationFields', [
            { id: '30009313905171', value: this.currentUser.email },
            {
              id: '26287517467923',
              value: `${this.currentUser.first_name} ${this.currentUser.last_name}`,
            },
          ]);
        }
        zenDeskwidget('messenger', 'show');
        zenDeskwidget('messenger', 'open');
      });
  }
}
